import React from "react";
import { motion } from "framer-motion";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export default function FloatingIcon({
                                         className,
                                         src,
                                         initialTopPct,
                                         initialLeftPct,
                                         speed = "4s",
                                         maxRadius = 60,
                                         chaseRadius = 90,
                                         width= '100px'
                                     }) {
    const ref = React.useRef(null);
    const parentRef = React.useRef(null);

    const [pos, setPos] = React.useState({ top: 0, left: 0 });
    const [inited, setInited] = React.useState(false);
    const baseRef = React.useRef({ top: 0, left: 0 });
    const posRef = React.useRef(pos);
    const lastMoveRef = React.useRef(0);
    const returnTimer = React.useRef(null);

    const appearDuration = React.useMemo(() => 1.25 + Math.random() * 0.45, []);
    const appearDelay = React.useMemo(() => 1 + Math.random() * 1, []);
    const startY = React.useMemo(() => 60 + Math.random() * 80, []);

    React.useEffect(() => {
        posRef.current = pos;
    }, [pos]);

    React.useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;

        parentRef.current = el.offsetParent || el.parentElement || document.body;

        const recalc = () => {
            if (!parentRef.current) return;
            const pr = parentRef.current.getBoundingClientRect();
            const base = {
                top: (parseFloat(initialTopPct) / 100) * pr.height,
                left: (parseFloat(initialLeftPct) / 100) * pr.width,
            };
            const cur = posRef.current;
            const oldBase = baseRef.current;
            const offset = { x: cur.left - oldBase.left, y: cur.top - oldBase.top };
            baseRef.current = base;
            setPos({ left: base.left + offset.x, top: base.top + offset.y });
            setInited(true);
        };

        recalc();

        let ro;
        if (window.ResizeObserver && parentRef.current) {
            ro = new ResizeObserver(recalc);
            ro.observe(parentRef.current);
        }
        window.addEventListener("resize", recalc);

        return () => {
            if (ro) ro.disconnect();
            window.removeEventListener("resize", recalc);
        };
    }, [initialTopPct, initialLeftPct]);

    const relocate = React.useCallback(
        (cursor) => {
            const now = Date.now();
            if (now - lastMoveRef.current < 100) return;
            lastMoveRef.current = now;

            const el = ref.current;
            if (!el || !parentRef.current) return;

            const ir = el.getBoundingClientRect();
            const base = baseRef.current;
            const cur = posRef.current;
            const center = { x: cur.left + ir.width / 2, y: cur.top + ir.height / 2 };

            let angle = Math.random() * Math.PI * 2;
            if (cursor) {
                const dx = center.x - cursor.x;
                const dy = center.y - cursor.y;
                const away = Math.atan2(dy, dx);
                angle = away + (Math.random() - 0.5) * (Math.PI / 2);
            }

            const dist = (0.5 + Math.random() * 0.5) * maxRadius;
            const target = {
                left: base.left + dist * Math.cos(angle),
                top: base.top + dist * Math.sin(angle),
            };

            const pr = parentRef.current.getBoundingClientRect();
            const pad = 4;
            const maxX = pr.width - ir.width - pad;
            const maxY = pr.height - ir.height - pad;
            target.left = clamp(target.left, pad, maxX);
            target.top = clamp(target.top, pad, maxY);

            setPos(target);

            clearTimeout(returnTimer.current);
            returnTimer.current = setTimeout(() => setPos(baseRef.current), 1200);
        },
        [maxRadius]
    );

    const onEnter = (e) => relocate({ x: e.clientX, y: e.clientY });

    React.useEffect(() => {
        const parent = parentRef.current;
        if (!parent) return;

        const onMove = (e) => {
            const el = ref.current;
            if (!el) return;
            const ir = el.getBoundingClientRect();
            const center = {
                x: posRef.current.left + ir.width / 2,
                y: posRef.current.top + ir.height / 2,
            };
            const d = Math.hypot(e.clientX - center.x, e.clientY - center.y);
            if (d < chaseRadius) relocate({ x: e.clientX, y: e.clientY });
        };

        parent.addEventListener("mousemove", onMove);
        return () => parent.removeEventListener("mousemove", onMove);
    }, [chaseRadius, relocate]);

    const appearVariants = {
        initial: (dy) => ({ opacity: 0, y: dy, scale: 0.96, filter: "blur(4px)" }),
        animate: { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" },
    };

    const preInitStyle = {
        position: "absolute",
        top: `${initialTopPct}%`,
        left: `${initialLeftPct}%`,
        visibility: "hidden",
        "--speed": speed,
    };

    const postInitStyle = {
        position: "absolute",
        top: pos.top,
        left: pos.left,
        "--speed": speed,
    };

    return (
        <motion.div
            ref={ref}
            className={className}
            custom={startY}
            variants={appearVariants}
            initial="initial"
            whileInView={inited ? "animate" : "initial"}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
                duration: appearDuration,
                delay: appearDelay,
                ease: "easeOut",
            }}
            style={inited ? postInitStyle : preInitStyle}
            onMouseEnter={onEnter}
        >
            <img style={{width:width}} src={src} alt="" />
        </motion.div>
    );
}
